import React, { useEffect, useState } from "react";
import { FaApple, FaBitcoin, FaEthereum, FaLinux, FaWindows } from "react-icons/fa";
import { SiBinance, SiNvidia } from "react-icons/si";
import { MdQrCode2, MdSearch } from "react-icons/md";
import { HiOutlineDownload } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";
import mobile from "../assets/images/phones.png";
import qr from "../assets/images/qr.jpg";
import mediaOne from "../assets/images/11.png";
import mediaTwo from "../assets/images/22.png";
import mediaThree from "../assets/images/33.png";
import Header from "../components/Header";
import "./Home.css";
import Footer from "../components/Footer";
import { useContent, useFaqs } from "../components/SiteConfigContext";
import env from "../services/env";
import { marketChange, marketName, marketPrice, marketSymbol, useMarketAssets } from "../services/useMarketAssets";
import { formatTickerChange, formatTickerPrice, useBinanceTicker } from "../services/BinanceTickerContext";

const coins = [
  [FaBitcoin, "BTC", "Bitcoin", "$83,636.00", "-2.75%", "coin-btc"],
  [FaEthereum, "ETH", "Ethereum", "$2,667.11", "-2.71%", "coin-eth"],
  [SiBinance, "BNB", "BNB", "$769.73", "-2.36%", "coin-bnb"],
  [null, "SPCXB", "SpaceX (bStocks)", "$147.72", "-4.30%", "coin-spacex"],
  [SiNvidia, "NVDAB", "NVIDIA (bStocks)", "$222.60", "-2.83%", "coin-nvidia"],
];

const defaultFaqs = [
  "Why choose Tillionx for crypto trading in India?",
  "How can I buy Bitcoin and other cryptocurrencies in India?",
  "How do I buy USDT via P2P?",
  "How to trade cryptocurrencies in India on Tillionx?",
  "What products does Tillionx offer for crypto users?",
  "How to earn passive income from crypto on Tillionx?",
  "How to trade safely on Tillionx P2P?",
  "How to track cryptocurrency prices?",
  "How do I avoid scams and keep my Tillionx account secure in India?",
].map((question) => ({ question, answer: "" }));

const tradeLink = (symbol, market) => `/trade?symbol=${encodeURIComponent(market?.market_symbol ?? market?.symbol ?? `${symbol}USDT`)}`;

function MarketCoinRow({ coin }) {
  const [Icon, symbol, name, fallbackPrice, fallbackChange, className] = coin;
  const ticker = useBinanceTicker(symbol);

  return (
    <Link className="coin-row" to={tradeLink(symbol)} style={{ color: "inherit", textDecoration: "none" }}>
      <div className="coin-name">{Icon ? <Icon className={className} /> : <i className={className}>X</i>}<b>{symbol}</b><span>{name}</span></div>
      <strong>{formatTickerPrice(ticker, fallbackPrice)}</strong><em>{formatTickerChange(ticker, fallbackChange)}</em>
    </Link>
  );
}

function AssetCoinRow({ market }) {
  const symbol = marketSymbol(market);
  const ticker = useBinanceTicker(symbol);
  const change = marketChange(ticker);
  return (
    <Link className="coin-row" to={tradeLink(symbol, market)} style={{ color: "inherit", textDecoration: "none" }}>
      <div className="coin-name"><b>{symbol}</b><span>{marketName(market)}</span></div>
      <strong>{marketPrice(ticker)}</strong><em>{change}</em>
    </Link>
  );
}

const marketTabs = [
  ["popular", "Popular"],
  ["new", "New Listing"],
  ["stocks", "Stocks"],
  ["commodities", "tCommodities"],
];

function MarketCard() {
  const [tab, setTab] = useState("popular");
  const { assets } = useMarketAssets();
  const categoryOf = (market) => String(market.category ?? "");
  const rows = tab === "new"
    ? [...assets].sort((left, right) => new Date(right.created_at ?? 0) - new Date(left.created_at ?? 0)).slice(0, 5)
    : tab === "stocks"
      ? assets.filter((market) => /stock/i.test(categoryOf(market))).slice(0, 5)
      : tab === "commodities"
        ? assets.filter((market) => /commodit/i.test(categoryOf(market))).slice(0, 5)
        : [];

  return (
    <div className="market-stack">
      <section className="market-card">
        <div className="card-tabs">
          {marketTabs.map(([key, label]) => {
            const Tag = key === tab ? "strong" : "span";
            return <Tag key={key} role="button" tabIndex={0} style={{ cursor: "pointer" }} onClick={() => setTab(key)} onKeyDown={(event) => { if (event.key === "Enter") setTab(key); }}>{label}</Tag>;
          })}
          <small><Link to="/markets/overview" style={{ color: "inherit" }}>View All ›</Link></small>
        </div>
        {tab === "popular"
          ? coins.map((coin) => <MarketCoinRow key={coin[1]} coin={coin} />)
          : rows.length ? rows.map((market) => <AssetCoinRow key={marketSymbol(market)} market={market} />) : <p>No listings available yet.</p>}
      </section>
      <section className="news-card">
        <div className="news-heading"><strong>News</strong><small>View All News ›</small></div>
        <p>Saudi Oil Export Value Falls 12.8% in July</p>
        <p>Former Dallas Fed President Kaplan Says Markets Are Too Aggressive on Rate-Hike Expectations</p>
        <p>ECB’s Schnabel Says Iran War Energy Shock Is More Persistent</p>
        <p>Contentos to Shut Down COS.TV and Channel VIP on December 14, 2026</p>
      </section>
    </div>
  );
}

function useUserCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () => fetch(`${env.url}/stats/users`, { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((body) => { if (active && Number.isFinite(Number(body?.data?.count))) setCount(Number(body.data.count)); })
      .catch(() => {});
    load();
    const timer = window.setInterval(load, 30000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);
  return count;
}

function Home() {
  const navigate = useNavigate();
  const userCount = useUserCount();
  const [openFaq, setOpenFaq] = useState(null);
  const faqs = useFaqs("home", defaultFaqs);
  const faqHeading = useContent("home_faq_title", "Frequently Asked Questions");
  const heroTitle = useContent("home_hero_title", "");

  return (
    <main className="tillionx-home">
      <Header />

      <section className="hero-section" id="markets">
        <div className="hero-copy">
          <div className="user-count">{(userCount ?? 1359).toLocaleString("en-US")}</div><h1>{heroTitle || <>USERS<br />TRUST US</>}</h1>
          <div className="awards"><div>❮ <b>No.1</b> ❯<small>Customer Assets</small></div><div>❮ <b>No.1</b> ❯<small>Trading Volume</small></div></div>
          <div className="hero-actions"><div className="security">▦ &nbsp; Industry-Leading Security</div><button onClick={() => navigate("/signup")}>Sign Up</button></div>
          <div className="social-actions"><button><span className="google-g">G</span></button><button><FaApple /></button><i></i><button><MdQrCode2 /></button></div>
        </div>
        <MarketCard />
      </section>

      <section className="trust-strip"><div>Forbes<small>Recognized as Forbes’ Most Trusted Crypto Exchanges 2025</small></div><div>CNBC<small>Named CNBC’s World's Top Fintech Companies 2025 in Digital Assets</small></div><div>FORTUNE<small>Named a CeFi Leader in Fortune Crypto 100's most influential companies in blockchain</small></div></section>

      <section className="safu-section"><div><h2>FUNDS ARE<br /><span>SAFU</span></h2><p>The Security of User Assets Fund (SAFU) was established in 2018 to protect your funds in rare emergencies. Your security is our priority.</p></div><div className="safu-stats"><p>As of February 2026, the SAFU fund wallet comprises a reserve of</p><strong>15,000 BTC</strong><small>SAFU Wallet: 1BAuq7Vho2CEkVkUxbfU26LhwQjbCmWQKd</small><div><b>7,488,223<small>Users helped</small></b><b>$229,433,449<small>Funds recovered</small></b></div></div></section>

      <section className="media-grid"><div><img src={mediaOne} alt="Community celebration" /><button>▶</button></div><div><img src={mediaTwo} alt="Friends enjoying a meal" /><button>▶</button></div><div><img src={mediaThree} alt="Road trip" /><button>▶</button></div></section>

      <section className="download-section" id="buy"><div className="phone-wrap"><img src={mobile} alt="Tillionx mobile trading interface" /><div className="download-tabs"><b>Mobile</b><span>Desktop</span></div></div><div className="download-copy"><h2>Trade on the go. Anywhere,<br />anytime.</h2><div className="qr-row"><img src={qr} alt="Download QR code" /><p>Scan to Download App<strong>iOS and Android</strong></p></div><div className="platforms"><span><FaApple />MacOS</span><span><FaWindows />Windows</span><span><FaLinux />Linux</span></div><a href="#download"><HiOutlineDownload /> More Download Options</a></div></section>

      <section className="faq-section"><h2>{faqHeading}</h2>{faqs.map(({ question, answer }, index) => <div className="faq-row" key={question} onClick={() => setOpenFaq(openFaq === index ? null : index)}><span>{index + 1}</span><div><b>{question}</b>{openFaq === index && <p>{answer || "Find answers and helpful guidance from Tillionx Support."}</p>}</div><strong>{openFaq === index ? "−" : "+"}</strong></div>)}</section>
      <section className="signup-band"><h2>Secure, Low-Fee Trading on Tillionx</h2><button onClick={() => navigate("/signup")}>Sign Up Now</button></section>
      <Footer />
    </main>
  );
}

export default Home;
